/* eslint-disable max-lines */
import { useToast } from '@cian/ui-kit';
import { IconFunctionalSuccess16 } from '@cian/ui-kit-design-tokens/icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useApplicationContext } from 'shared/common/utils/applicationContext';

import type { ISuggestionDefaultItem, ISuggestionItem } from '../components/OfferCalculatorSuggest';

import { useBaseField } from '../components/BaseField';
import {
  MIN_REGION_SUGGEST_CHARS,
  REGION_SUGGEST_API_ERROR_TOAST_TEXT,
  REGION_SUGGEST_DEBOUNCE_MS,
} from '../constants/regionSuggest';
import { setSelectedRegion } from '../redux';
import { selectRegionOptions, selectSelectedRegion } from '../selectors';
import { searchRegions } from '../services/searchRegions';
import { IEFieldSource, IField, IOption, ISelectedRegion } from '../types/calculator';
import { useAppDispatch } from './useAppDispatch';

const _findSuggestItemLabelByValue = (value: string | undefined, source: IEFieldSource[] = []) => {
  if (!value) {
    return '';
  }

  const item = source.find(item => item.value === value);

  return item ? item.label : '';
};

const _getCommittedRegionDisplay = (
  fieldValue: string,
  source: IEFieldSource[] | undefined,
  selectedRegion: ISelectedRegion | null,
) => {
  const v = String(fieldValue);

  if (!v) {
    return '';
  }

  if (selectedRegion && String(selectedRegion.regionId) === v) {
    return selectedRegion.displayName;
  }

  const fromSource = _findSuggestItemLabelByValue(v, source);

  if (fromSource) {
    return fromSource;
  }

  return '';
};

const EMPTY_REGIONS_OPTIONS: IOption[] = [];

export const useSuggestField = (props: IField) => {
  const { httpApi, logger } = useApplicationContext();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const selectedRegion = useSelector(selectSelectedRegion);
  const regionsOptions = useSelector(selectRegionOptions) ?? EMPTY_REGIONS_OPTIONS;
  const { field, fieldState } = useBaseField(props);

  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() =>
    _getCommittedRegionDisplay(String(field.value), props.source, selectedRegion),
  );
  const [remoteSuggestions, setRemoteSuggestions] = useState<ISuggestionDefaultItem<string>[]>([]);

  const lastPickedRef = useRef<{ value: string; label: string; parentId: number | null } | null>(null);
  /** id из geo саджеста → parentId (если null — у локации нет родителя). */
  const catalogParentByIdRef = useRef<Map<string, number | null>>(new Map());
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const regionSearchContainerRef = useRef<HTMLDivElement>(null);
  const pendingRemoteSearchRef = useRef(0);
  const [isRegionRemoteSearchLoading, setIsRegionRemoteSearchLoading] = useState(false);

  const resetRegionRemoteSearchLoading = useCallback(() => {
    pendingRemoteSearchRef.current = 0;
    setIsRegionRemoteSearchLoading(false);
  }, []);

  const trackRemoteSearchStart = useCallback(() => {
    pendingRemoteSearchRef.current += 1;
    setIsRegionRemoteSearchLoading(true);
  }, []);

  const trackRemoteSearchEnd = useCallback(() => {
    pendingRemoteSearchRef.current = Math.max(0, pendingRemoteSearchRef.current - 1);
    if (pendingRemoteSearchRef.current === 0) {
      setIsRegionRemoteSearchLoading(false);
    }
  }, []);

  const clearSearchTimeout = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, []);

  const restoreCommittedDisplay = useCallback(() => {
    const v = String(field.value);
    const committedDisplay = _getCommittedRegionDisplay(v, props.source, selectedRegion);

    if (committedDisplay) {
      setInputValue(committedDisplay);

      return;
    }

    if (lastPickedRef.current?.value === v) {
      setInputValue(lastPickedRef.current.label);

      return;
    }

    setInputValue('');
  }, [field.value, props.source, selectedRegion]);

  useEffect(() => {
    return () => {
      clearSearchTimeout();
    };
  }, [clearSearchTimeout]);

  useEffect(() => {
    const v = String(field.value);

    if (lastPickedRef.current && lastPickedRef.current.value !== v) {
      lastPickedRef.current = null;
    }

    const committedDisplay = _getCommittedRegionDisplay(v, props.source, selectedRegion);

    if (committedDisplay) {
      setInputValue(committedDisplay);

      return;
    }

    if (lastPickedRef.current?.value === v) {
      setInputValue(lastPickedRef.current.label);

      return;
    }

    setInputValue('');
  }, [field.value, props.source, selectedRegion]);

  /** Синхронизация `selectedRegion` после SSR/гидрации, когда в форме уже есть `regionId`, а в сторе — ещё null. */
  useEffect(() => {
    const v = String(field.value);

    if (!v) {
      if (selectedRegion !== null) {
        dispatch(setSelectedRegion(null));
      }

      return;
    }

    // Эффект нужен только для первичной гидрации, когда в сторе ещё нет selectedRegion.
    // После пользовательского выбора не перезаписываем стор из локального source, иначе возможен цикл при гонке обновлений.
    if (selectedRegion !== null) {
      return;
    }

    const fromSource = props.source?.find(item => item.value === v);

    if (fromSource) {
      dispatch(
        setSelectedRegion({
          regionId: Number(fromSource.value),
          displayName: fromSource.label,
        }),
      );
    }
  }, [field.value, selectedRegion, dispatch, props.source]);

  const runRemoteSearch = useCallback(
    async (text: string) => {
      const trimmed = text.trim();

      if (trimmed.length < MIN_REGION_SUGGEST_CHARS) {
        setRemoteSuggestions([]);

        return;
      }

      const requestId = ++searchRequestIdRef.current;
      trackRemoteSearchStart();
      try {
        const regions = await searchRegions({ httpApi, logger, text: trimmed });

        if (requestId !== searchRequestIdRef.current) {
          return;
        }

        const nextMeta = new Map<string, number | null>();
        regions.forEach(({ id, parentId }) => {
          nextMeta.set(String(id), parentId);
        });
        catalogParentByIdRef.current = nextMeta;

        setRemoteSuggestions(
          regions.map(({ id, displayName }) => ({
            value: String(id),
            title: displayName,
            type: 'default' as const,
            rightIcon:
              String(id) === String(field.value) ? <IconFunctionalSuccess16 color="icon-main-default" /> : undefined,
          })),
        );
      } catch (error) {
        if (requestId !== searchRequestIdRef.current) {
          return;
        }

        logger.error(error as Error);
        setRemoteSuggestions([]);
        toast.open({
          type: 'info',
          text: REGION_SUGGEST_API_ERROR_TOAST_TEXT,
          dataTestId: 'offer-calculator-region-suggest-api-error-toast',
        });
      } finally {
        trackRemoteSearchEnd();
      }
    },
    [httpApi, logger, field.value, trackRemoteSearchStart, trackRemoteSearchEnd, toast],
  );

  const scheduleRemoteSearch = useCallback(
    (text: string) => {
      clearSearchTimeout();
      searchTimeoutRef.current = setTimeout(() => {
        void runRemoteSearch(text);
      }, REGION_SUGGEST_DEBOUNCE_MS);
    },
    [clearSearchTimeout, runRemoteSearch],
  );

  const handleSuggestSelect = (item: ISuggestionItem<string>) => {
    const meta = catalogParentByIdRef.current;
    const parentForRef = meta.has(item.value) ? (meta.get(item.value) ?? null) : null;
    lastPickedRef.current = { value: item.value, label: item.title, parentId: parentForRef };
    setInputValue(item.title);
    setIsSuggestOpen(false);

    const next: ISelectedRegion = { regionId: Number(item.value), displayName: item.title };
    if (meta.has(item.value)) {
      next.parentId = meta.get(item.value) ?? null;
    }
    field.onChange(item.value);
    dispatch(setSelectedRegion(next));
    clearSearchTimeout();
    resetRegionRemoteSearchLoading();
    setRemoteSuggestions([]);
  };

  const handleSuggestClose = () => {
    setIsSuggestOpen(false);
    clearSearchTimeout();
    resetRegionRemoteSearchLoading();
    setRemoteSuggestions([]);
    restoreCommittedDisplay();
  };

  const handleInputFocus = () => {
    setIsSuggestOpen(true);
    setInputValue('');
    resetRegionRemoteSearchLoading();
    setRemoteSuggestions([]);
  };

  const focusRegionSearchInput = useCallback(() => {
    requestAnimationFrame(() => {
      regionSearchContainerRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    });
  }, []);

  const handleRegionSearchReset = useCallback(() => {
    clearSearchTimeout();
    searchRequestIdRef.current += 1;
    resetRegionRemoteSearchLoading();
    setRemoteSuggestions([]);
    setInputValue('');
    setIsSuggestOpen(true);
    focusRegionSearchInput();
  }, [clearSearchTimeout, resetRegionRemoteSearchLoading, focusRegionSearchInput]);

  const handleInputChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setInputValue(value);

    if (value.trim().length < MIN_REGION_SUGGEST_CHARS) {
      setRemoteSuggestions([]);
      resetRegionRemoteSearchLoading();
    }

    scheduleRemoteSearch(value);

    if (!isSuggestOpen) {
      setIsSuggestOpen(true);
    }
  };

  const suggestions: ISuggestionDefaultItem<string>[] = useMemo(() => {
    const trimmed = inputValue.trim();

    if (trimmed.length < MIN_REGION_SUGGEST_CHARS) {
      const q = trimmed.toLowerCase();
      const list = q ? regionsOptions.filter(o => o.label.toLowerCase().includes(q)) : regionsOptions;

      return list.map(item => ({
        value: item.value,
        title: item.label,
        type: 'default' as const,
        rightIcon:
          item.value === String(field.value) ? <IconFunctionalSuccess16 color="icon-main-default" /> : undefined,
      }));
    }

    return remoteSuggestions.map(item => ({
      ...item,
      rightIcon: item.value === String(field.value) ? <IconFunctionalSuccess16 color="icon-main-default" /> : undefined,
    }));
  }, [remoteSuggestions, regionsOptions, inputValue, field.value]);

  return {
    inputValue,
    error: fieldState.error,
    isError: fieldState.isDirty || fieldState.invalid,
    name: field.name,
    label: props.label,
    placeholder: props.placeholder,
    disabled: props.disabled,
    isSuggestOpen,
    handleSuggestSelect,
    handleSuggestClose,
    handleInputFocus,
    handleInputChange,
    suggestions,
    isRegionRemoteSearchLoading,
    regionSearchContainerRef,
    handleRegionSearchReset,
  };
};
